import { useEffect } from "react"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { Box, InputAdornment, TextField } from "@mui/material"
import { fetchMenusList, MenuPlaningQuery, MenusListResponse } from "../../menuPlanningApi"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { FormikAutoComplete } from "../../../../../../shared/components/forms/Formik"
import { CreateUpdateDayMenuFormValues } from "../../menuPlanningTypes"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useMerchant } from "../../../../../merchants/merchantHooks"
import { colorOptions } from "../helpers"

const useStyles = makeStyles()(() => ({
    optionWrap: {
        display: "flex",
        alignItems: "center",
        gap: ".5rem",
    },
    colorBox: {
        display: "inline-block",
        width: "1rem",
        height: "1rem",
        borderRadius: ".25rem",
    },
    select: {
        maxWidth: "11rem",
    },
}))

const selectMenus = (data: MenusListResponse) => {
    const menuPlanning = data?.menuPlanning
    if (!menuPlanning || !menuPlanning.weeks) {
        return []
    }
    const calendarEvents = menuPlanning.weeks
        .flatMap((week) => week.dailyMenus)
        .filter((menu) => menu.isActive)
        .flatMap((menu) => [menu.color])
    return calendarEvents
}

export function FormikSelectColorField() {
    const { classes } = useStyles()
    const { merchantId } = useMerchant()
    const { values, setFieldValue } = useFormikContext<CreateUpdateDayMenuFormValues>()

    const { data: events = [] } = useApiQuery(fetchMenusList, {
        queryName: MenuPlaningQuery.LIST,
        dependencies: {
            merchantId,
            from: values.publishDate.toISODate(),
            to: values.publishDate.toISODate(),
        },
        select: selectMenus,
    })

    const availableColors = colorOptions.filter((option) => !events.includes(option.code))

    useEffect(() => {
        if (availableColors.length > 0 && !values.color) {
            setFieldValue("color", availableColors[0])
        }
    }, [availableColors.length])

    return (
        <Box className={classes.select}>
            <LocalizedStrict id="merchant-menu-planning-color-field-label" attrs={{ label: true }}>
                <FormikAutoComplete
                    name="color"
                    label="Color"
                    options={colorOptions}
                    getOptionLabel={(option) => option.name}
                    renderOption={(props, option) => (
                        <Box component="li" {...props}>
                            <Box className={classes.optionWrap}>
                                <span className={classes.colorBox} style={{ backgroundColor: option.code }} />
                                {option.name}
                            </Box>
                        </Box>
                    )}
                    fullWidth
                    renderInput={() => <TextField />}
                    InputProps={{
                        ...(values.color?.code && {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Box className={classes.colorBox} style={{ backgroundColor: values.color?.code }} />
                                </InputAdornment>
                            ),
                        }),
                    }}
                />
            </LocalizedStrict>
        </Box>
    )
}
